import { AssessmentStateEntry } from "@pandas/assessment-engine";
import { calculateLocale, languageToi18nCode } from "@pandas/core-ui";
import type { Partner, PartnerSettings, Session } from "@pandas/http-client";
import { mapAssessmentResults } from "@/lib/assessments/assessmentResultsMappings";
import type { InitSearch } from "@/routes/$locale/$partner/-search-params";
import { platformApi } from "@/services";
import type { ConfiguratorConfig } from "@/services/types";
import { useStore } from "@/store";
import { filterMetadataParams } from "@/utils/filterMetadataParams";
import { evaluateAssessmentRetention } from "./evaluateAssessmentRetention";
import { FlowType } from "./flowTypeHelpers";
import { handleAssessmentDetailsByIdentifier, handleDeviceDetails } from "./handleSessionDetails";
import { initializeSteps } from "./stepManagerV2";

const isFlowType = (flow: string): flow is FlowType => {
	return Object.values(FlowType).includes(flow as FlowType);
};

export const flowInitializer = async (
	configurationResponse: ConfiguratorConfig,
	partnerSettings: PartnerSettings,
	partner: Partner,
	queryParams: InitSearch,
	prefetchedSession?: Session | null,
) => {
	const sessionId = useStore.getState().sessionId;
	const setFlow = useStore.getState().setFlow;
	const setSessionDetails = useStore.getState().setSessionDetails;
	const setSessionId = useStore.getState().setSessionId;
	const setInitializedScreens = useStore.getState().setInitializedScreens;

	let flow = FlowType.default;
	let language = "en";
	let sessionDetails: Session | null = null;
	let storedAssessmentResultsTimestamp: Date | null = null;
	let functionalResults: Array<AssessmentStateEntry> = [];
	let graderResults: Array<AssessmentStateEntry> = [];

	language =
		sessionStorage.getItem("selectedLanguage") ?? languageToi18nCode(partnerSettings.languages[0] ?? "") ?? "en";

	if (queryParams?.flow && isFlowType(queryParams.flow)) flow = queryParams.flow;

	if (queryParams?.sessionId) {
		setSessionId(queryParams.sessionId);
		sessionDetails = prefetchedSession ?? (await platformApi.getSessionById<Session>(queryParams.sessionId));
		if (queryParams.flow === "flex" || queryParams.flow === "vision-ai")
			if (sessionDetails.has_cosmetic_tests)
				if (sessionDetails.last_location_id) flow = FlowType.cosmeticFirstFromStore;
				else flow = FlowType.cosmeticFirstFromHome;
			else flow = FlowType.functionalFirstFromStore;
		language = sessionStorage.getItem("selectedLanguage") ?? sessionDetails.locale;
		setSessionDetails(sessionDetails);
		if (sessionDetails.Assessment) graderResults = mapAssessmentResults(sessionDetails.Assessment, "cosmetic");

		if (sessionDetails.Identification) {
			handleDeviceDetails(sessionDetails.Identification);
			const assessmentDetailsByIdentifier = await handleAssessmentDetailsByIdentifier(
				partner.id,
				sessionDetails.Identification.identifier ?? "",
				configurationResponse.assessments,
			);
			storedAssessmentResultsTimestamp = assessmentDetailsByIdentifier.timestamp;
			functionalResults = assessmentDetailsByIdentifier.functionalResults;
		}
	} else {
		if (!sessionId) {
			const session = await platformApi.createSession({
				partnerId: partner.id,
				initiator: "webapp",
				locale: calculateLocale(language, partner.country ?? undefined),
				externalIntegrationIdentifier: queryParams?.eii ?? undefined,
				locationId: queryParams?.locationId,
				metadata: filterMetadataParams(queryParams) as Record<string, string> | undefined,
			});
			setSessionId(session.id);
		}
	}
	setFlow(flow);
	sessionStorage.setItem("selectedLanguage", language);
	const shouldKeepAssessmentResults = evaluateAssessmentRetention(
		partnerSettings.assessmentExpiryDays,
		storedAssessmentResultsTimestamp,
	);

	const initializedScreens = initializeSteps(
		configurationResponse.pages,
		configurationResponse.assessments,
		flow,
		configurationResponse.tradeInCompletionPage.finalizeOnVision,
		configurationResponse.redirection_options.step,
		shouldKeepAssessmentResults,
		queryParams?.imei ? true : false,
	);

	setInitializedScreens(initializedScreens);

	return {
		flow: flow,
		graderResults: graderResults,
		functionalResults: functionalResults,
		shouldKeepAssessmentResults: shouldKeepAssessmentResults,
	};
};
